// packages/chatbot/src/utils/pageContext.ts

import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

export type FullPageContextOptions = {
  maxChars?: number;
  includeLinks?: boolean;
  scrubSelectors?: string[];
};

export type PageContextPack = {
  page: {
    url: string;
    path: string;
    title: string;
    canonical: string | null;
    lang: string | null;
    referrer: string | null;
    meta: {
      description: string | null;
      og_title: string | null;
      og_description: string | null;
    };
  };
  headings: string[];
  links: Array<{ text: string; href: string }>;
  content_markdown: string;
  selection: string | null;
  timestamp_utc: string;
  size_chars: number;
  content_hash: string;
};

export const defaultPrivacyScrubSelectors: string[] = [
  'script', 'style', 'noscript', 'template',
  'nav', 'footer', 'aside', '.sidebar', '.menu', '.breadcrumb', '.skip-link',
  '.ad', '.ads', '.advert', '.banner',
  '.cookie', '.cookies', '#cookie-banner', '#onetrust-consent-sdk',
  '.consent', '.gdpr', '.cc-window',
  '.modal', '[aria-modal="true"]', '.popup', '.dialog', '.lightbox', '.subscription', '.newsletter',
  '#intercom-container', '.intercom-container', '.crisp-client',
  '#tidio-chat', '.tidio-chat',
  '#drift-widget', '#drift-frame-controller',
  '#fb-root',
  '.hubspot-messages',
  '.hs-form', '.hubspot',
  '.klaviyo-form', '.mailchimp', '.convertkit',
  '.g-recaptcha', '.grecaptcha-badge',
  '#payment', '.payment', '[data-payment]', '[data-stripe]', '[data-paypal]',
  'iframe', 'embed', 'object',
];

const ctxCache: { key?: string; pack?: PageContextPack } = {};

function quickHash(str: string): string {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return (h >>> 0).toString(36);
}

function metaContent(d: Document, name: string): string | null {
  const byName = d.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (byName?.content) return byName.content;
  const byOg = d.querySelector(`meta[property="og:${name}"]`) as HTMLMetaElement | null;
  return byOg?.content || null;
}

function stripNoise(root: HTMLElement, extra: string[]) {
  const dropSel = [...defaultPrivacyScrubSelectors, ...extra].join(',');
  if (dropSel.trim().length) root.querySelectorAll(dropSel).forEach((el) => el.remove());
  root.querySelectorAll('form').forEach((el) => el.remove());

  const inputSel = [
    'input', 'textarea', 'select',
    'input[type="password"]',
    'input[name*="password" i]', '[name*="token" i]', '[name*="ssn" i]',
    '[name*="credit" i]', '[name*="card" i]', '[name*="cvv" i]', '[name*="cvc" i]',
    '[data-sensitive]', '[data-private]', '[data-scrub]',
  ].join(',');
  root.querySelectorAll(inputSel).forEach((el: Element) => {
    const span = document.createElement('span');
    span.textContent = '';
    el.replaceWith(span);
  });
}

function buildMarkdownFrom(el: HTMLElement, maxChars: number): string {
  const td = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
    emDelimiter: '*',
    strongDelimiter: '**',
  });
  td.use(gfm);
  td.keep(['table', 'thead', 'tbody', 'tr', 'th', 'td', 'pre', 'code']);
  let md = td.turndown(el as unknown as HTMLElement);
  md = md.replace(/\n{3,}/g, '\n\n').replace(/[ \t]+\n/g, '\n').trim();
  if (md.length > maxChars) md = md.slice(0, maxChars - 1) + '…';
  return md;
}

export function collectFullPageContextMarkdown(options?: FullPageContextOptions): PageContextPack {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      page: {
        url: '',
        path: '',
        title: '',
        canonical: null,
        lang: null,
        referrer: null,
        meta: { description: null, og_title: null, og_description: null },
      },
      headings: [],
      links: [],
      content_markdown: '',
      selection: null,
      timestamp_utc: new Date().toISOString(),
      size_chars: 0,
      content_hash: quickHash(''),
    };
  }

  const w = window;
  const d = document;

  const maxChars = options?.maxChars ?? 20000;
  const includeLinks = options?.includeLinks ?? true;
  const extraScrub = options?.scrubSelectors ?? [];

  const main =
    d.querySelector('main, article, [role="main"], .content, .post, .entry-content') || d.body;
  const clone = main.cloneNode(true) as HTMLElement;

  stripNoise(clone, extraScrub);

  const pathKey = `${w.location.pathname}|${d.title}|${maxChars}|${includeLinks}|${extraScrub.join(',')}`;
  const sample = (clone.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 200_000);
  const cacheKey = `${pathKey}|${quickHash(sample)}`;

  if (ctxCache.key === cacheKey && ctxCache.pack) {
    return ctxCache.pack;
  }

  const headings: string[] = [];
  clone.querySelectorAll('h1,h2,h3,h4').forEach((h) => {
    const t = (h.textContent || '').trim();
    if (t) headings.push(t);
  });

  const links: Array<{ text: string; href: string }> = [];
  if (includeLinks) {
    const origin = w.location.origin;
    const seen = new Set<string>();
    clone.querySelectorAll('a[href]').forEach((a) => {
      try {
        const href = (a as HTMLAnchorElement).getAttribute('href') || '';
        const u = new URL(href, origin);
        if (u.origin === origin && !seen.has(u.href) && links.length < 20) {
          seen.add(u.href);
          links.push({ text: (a.textContent || '').trim().slice(0, 120), href: u.href });
        }
      } catch {
        // ignore
      }
    });
  }

  const content_markdown = buildMarkdownFrom(clone, maxChars);
  const sel = w.getSelection?.()?.toString()?.slice(0, 1200) || '';

  const pack: PageContextPack = {
    page: {
      url: w.location.href,
      path: w.location.pathname,
      title: d.title || '',
      canonical: (d.querySelector('link[rel="canonical"]') as HTMLLinkElement | null)?.href || null,
      lang: d.documentElement.lang || null,
      referrer: d.referrer || null,
      meta: {
        description: metaContent(d, 'description'),
        og_title: metaContent(d, 'title'),
        og_description: metaContent(d, 'description'),
      },
    },
    headings,
    links,
    content_markdown,
    selection: sel || null,
    timestamp_utc: new Date().toISOString(),
    size_chars: content_markdown.length,
    content_hash: quickHash(content_markdown),
  };

  ctxCache.key = cacheKey;
  ctxCache.pack = pack;
  return pack;
}
